import { create } from 'zustand';
import { holdSkinsApi, type HoldSkinEntry } from './holdSkinsApi';

interface HoldSkinState {
  items: HoldSkinEntry[];
  coins: number;
  gems: number;
  equippedId: string | null;
  equippedUrl: string | null;
  defaultUrls: string[];
  loaded: boolean;
  refresh: () => Promise<void>;
  load: () => Promise<void>;
  buy: (id: string, currency: 'coins' | 'gems') => Promise<void>;
  equip: (id: string) => Promise<void>;
  unequip: () => Promise<void>;
  /** Resolve the shape url to use for ONE match: equipped skin, else a random
   * pick from the free default pool, else null (built-in drawn arc). */
  resolveMatchUrl: () => string | null;
}

let inflight: Promise<void> | null = null;

export const useHoldSkinStore = create<HoldSkinState>((set, get) => {
  const fetchList = async () => {
    try {
      const r = await holdSkinsApi.list();
      set({
        items: r.items,
        coins: r.coins,
        gems: r.gems,
        equippedId: r.equippedId,
        equippedUrl: r.equippedUrl,
        defaultUrls: r.defaultUrls,
        loaded: true,
      });
    } catch {
      set({ loaded: true });
    } finally {
      inflight = null;
    }
  };

  return {
    items: [],
    coins: 0,
    gems: 0,
    equippedId: null,
    equippedUrl: null,
    defaultUrls: [],
    loaded: false,
    refresh: () => { inflight = fetchList(); return inflight; },
    load: () => {
      if (get().loaded) return Promise.resolve();
      if (!inflight) inflight = fetchList();
      return inflight;
    },
    buy: async (id, currency) => { await holdSkinsApi.buy(id, currency); await get().refresh(); },
    equip: async (id) => { await holdSkinsApi.equip(id); await get().refresh(); },
    unequip: async () => { await holdSkinsApi.unequip(); await get().refresh(); },
    resolveMatchUrl: () => {
      const { equippedUrl, defaultUrls } = get();
      if (equippedUrl) return equippedUrl;
      if (defaultUrls.length > 0) {
        return defaultUrls[Math.floor(Math.random() * defaultUrls.length)] ?? null;
      }
      return null;
    },
  };
});
